import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useFollowStream } from "./useFollowStream";
import { NewMessagesHint } from "./components/NewMessagesHint";
import { loadPairings, addPairing, removePairing, decodePairingPayload, type PairingPayload } from "./pairings";
import { forgetPairingReads } from "./groupReads";
import { PairingClient, cardStates } from "./protocol";
import { enableWebPush } from "./push";
import { MirrorModal } from "./components/MirrorModal";
import { useHashRouter } from "./hooks/useHashRouter";
import { useBoot } from "./hooks/useBoot";
import { Lightbox, DocReader, type DocRevision } from "./components/Overlays";
import type { DocComment } from "./review";
import { Sidebar } from "./components/Sidebar";
import { Dashboard } from "./components/Dashboard";
import { ChatHeader } from "./components/ChatHeader";
import { ChatTabs } from "./components/ChatTabs";
import { ChatMessages } from "./components/ChatMessages";
import { Composer } from "./components/Composer";
import { IdeaBoard } from "./components/IdeaBoard";
import { IdeaThreadHeader } from "./components/IdeaThreadHeader";
import { IdeaMessages } from "./components/IdeaMessages";
import { TodosRail, SpecRail } from "./components/Rails";
import { CommandPalette } from "./components/CommandPalette";
import { PairPanel } from "./components/PairPanel";
import { ImagePreviewModal } from "./components/ImagePreviewModal";
import { useSessionTodos } from "./hooks/useSessionTodos";
import { useSessionAgents } from "./hooks/useSessionAgents";
import { MAX_ATTACHMENT_BYTES, humanSize } from "./attachments";
import { useIdeaThread } from "./hooks/useIdeaThread";
import { useIdeaChannels } from "./hooks/useIdeaChannels";
import { useSelectedChat } from "./hooks/useSelectedChat";
import { useSessions } from "./hooks/useSessions";
import { useGroups } from "./hooks/useGroups";
import { useGroupUnreads } from "./hooks/useGroupUnreads";
import { useGroupThread } from "./hooks/useGroupThread";
import { GroupThread } from "./components/GroupThread";
import { NewGroupModal } from "./components/NewGroupModal";
import { NewAgentModal } from "./components/NewAgentModal";
import { SessionGroupsBar } from "./components/SessionGroupsBar";
import { mentionName, type Mention } from "./mentions";

/**
 * The web client shell. A login-less peer: it holds mirrored pairing secrets and speaks the shared encrypted
 * Firestore protocol. This slice does chat (sidebar of session-contacts + a live, decrypted thread + send);
 * ideas / images / the QR key-mirror / web push land in later slices. Calling stays Android-only.
 */
/**
 * Scroll `node` to the top of its scroll container.
 *
 * Measured from the rendered boxes rather than `offsetTop`, which is relative to the nearest POSITIONED
 * ancestor — and the message list isn't one, so subtracting the container's own offset only approximated it
 * and landed tens of messages away from the target.
 */
function scrollNodeToTop(el: HTMLElement, node: HTMLElement): void {
  el.scrollTop += node.getBoundingClientRect().top - el.getBoundingClientRect().top;
}

/**
 * A ring buffer of what happened the last few times a chat was opened, readable from the console as
 * `failoverScrollLog()`.
 *
 * Here because a chat opening part-way up its history is INTERMITTENT and reproduces on nobody's machine on
 * demand: several effects can move the same scroll container, and after the fact there is no way to tell
 * which one did, or whether the list simply grew underneath. Recording the decision and the positions around
 * it turns "it sometimes breaks" into something answerable from the one run where it did.
 */
const scrollLog: string[] = [];
function noteScroll(event: string, detail: Record<string, unknown> = {}): void {
  const line = `${new Date().toISOString().slice(11, 23)} ${event} ${JSON.stringify(detail)}`;
  scrollLog.push(line);
  if (scrollLog.length > 200) scrollLog.shift();
}
(window as unknown as { failoverScrollLog: () => string }).failoverScrollLog = () => scrollLog.join("\n");

/**
 * Hold a list at its end while it settles, and return a canceller.
 *
 * One assignment isn't enough: the bottom moves for a while after a chat opens as images decode, fonts swap
 * and markdown lays out, each of which leaves a view that WAS at the end sitting above it. Re-asserting each
 * frame for a moment covers all of them without needing to know which one is in play.
 *
 * Any real input stops it immediately — the reader scrolling up during the settle must win, or this becomes
 * the very thing it is fixing.
 */
function holdAtBottom(el: HTMLElement, ms = 700): () => void {
  let raf = 0;
  let stop = false;
  const end = () => {
    stop = true;
    cancelAnimationFrame(raf);
    for (const ev of ["wheel", "touchstart", "keydown", "mousedown"] as const) el.removeEventListener(ev, end);
  };
  for (const ev of ["wheel", "touchstart", "keydown", "mousedown"] as const) el.addEventListener(ev, end, { passive: true });
  const started = performance.now();
  const pin = () => {
    if (stop) return;
    el.scrollTop = el.scrollHeight;
    if (performance.now() - started < ms) raf = requestAnimationFrame(pin);
    else end();
  };
  pin();
  return end;
}

export function App() {
  const { ready, err, setErr, pairings, setPairings } = useBoot();
  const { selected, setSelected, view, setView, openIdea, setOpenIdea } = useHashRouter();
  const [draft, setDraft] = useState("");
  const [pairInput, setPairInput] = useState("");
  const [showPair, setShowPair] = useState(false);
  const [showMirror, setShowMirror] = useState(false);
  // How many recent messages to load, PER CHAT — grows on scroll-up. Keyed by chat rather than held as one
  // number that a per-session effect resets: the limit is a subscription dependency, so resetting it after the
  // selection had already changed subscribed the new chat twice — once at the previous chat's depth, then
  // again at 50 — and the list shrank between the two. That mid-switch shrink is what threw the scroll
  // position off. Deriving it here means a switch already reads 50 in the same render, with one subscription.
  const [chatLimits, setChatLimits] = useState<Record<string, number>>({});
  const chatLimit = (selected ? chatLimits[selected] : undefined) ?? 50;
  const growChatLimit = useCallback((chat: string) => {
    setChatLimits((prev) => ({ ...prev, [chat]: (prev[chat] ?? 50) + 50 }));
  }, []);
  const [pendingImage, setPendingImage] = useState<{ files: File[]; urls: string[] } | null>(null); // preview before send
  const [imageCaption, setImageCaption] = useState("");
  const [sessionQuery, setSessionQuery] = useState(""); // filter the session list
  const [ideaQuery, setIdeaQuery] = useState(""); // filter the ideas list
  const [lightbox, setLightbox] = useState<string | null>(null); // full-size image overlay
  // The attached-document reader: which revision is open, its text, and the thread it belongs to so earlier
  // versions stay reachable. `channelId` is kept so switching revision can load a sibling from the same place.
  const [docReader, setDocReader] = useState<{
    title: string;
    content: string | null;
    loading: boolean;
    channelId: string;
    docId: string;
    revisions: DocRevision[];
    comments: DocComment[];
  } | null>(null);
  const [paletteOpen, setPaletteOpen] = useState(false); // ⌘K quick-jump palette
  const [paletteQuery, setPaletteQuery] = useState("");
  const [dragCol, setDragCol] = useState<string | null>(null); // kanban drop-target highlight
  const [selectedGroup, setSelectedGroup] = useState<string | null>(null); // "pid/gid" of the open #Group-chat
  const [showNewGroup, setShowNewGroup] = useState(false);
  const [showNewAgent, setShowNewAgent] = useState(false);
  const clients = useRef<Map<string, PairingClient>>(new Map());
  const { sessions, sessionsReady, clientsVersion, previews, unreads, sessionTodos } = useSessions(clients, ready, pairings, selected);
  const groups = useGroups(clients, ready, pairings);
  const { summaries: groupUnreads, unreadFromId: groupUnreadFromId } = useGroupUnreads(clients, groups, selectedGroup);
  const groupMessages = useGroupThread(clients, selectedGroup, clientsVersion);
  const todos = useSessionTodos(clients, selected, clientsVersion);
  const agents = useSessionAgents(clients, selected, clientsVersion);
  const ideaMessages = useIdeaThread(clients, selected, openIdea, clientsVersion);
  const channels = useIdeaChannels(clients, selected, clientsVersion);
  const { messages, chatReady, setChatReady, unreadFromId } = useSelectedChat(clients, selected, chatLimit, clientsVersion);
  const endRef = useRef<HTMLDivElement>(null);
  const ideaScrollRef = useRef<HTMLDivElement>(null);
  const groupScrollRef = useRef<HTMLDivElement>(null);
  const messagesRef = useRef<HTMLDivElement>(null);
  const nearBottomRef = useRef(true); // was the user pinned near the bottom before this update?
  const loadingOlderRef = useRef(false); // paging in older messages → preserve scroll, don't jump to bottom
  const prevHeightRef = useRef(0);

  // ⌘K / Ctrl+K toggles the quick-jump palette; Esc closes it.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPaletteQuery("");
        setPaletteOpen((o) => !o);
      } else if (e.key === "Escape") {
        setPaletteOpen(false);
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);



  // Reset paging + loading when switching sessions — and force the next load to land at the bottom (the
  // scroll refs otherwise stay stale from the previous session, leaving the chat stuck at the top).
  //
  // A LAYOUT effect, not a passive one: the page-back scroll restore below is itself a layout effect, and
  // layout effects run first. Reset passively and the restore fires on the new chat while
  // `loadingOlderRef` is still true from the old one, putting the view at a position computed from the
  // PREVIOUS chat's height — parked mid-history, with no divider to explain it.
  // The page limit is no longer reset here — it is derived per chat above, so it is already correct for the
  // new selection in this same render. Resetting it from here is what caused the double subscription.
  useLayoutEffect(() => {
    setChatReady(false);
    nearBottomRef.current = true;
    loadingOlderRef.current = false;
    prevHeightRef.current = 0;
  }, [selected]);


  // Reset the ideas view when switching sessions (channels come from useIdeaChannels).
  useEffect(() => {
    setView("chat");
    setOpenIdea(null);
  }, [selected]);

  // Paging in older messages keeps the reader's position rather than yanking to the bottom. Following the
  // newest is useFollowStream's job — it also decides when NOT to, and announces instead.
  useLayoutEffect(() => {
    const el = messagesRef.current;
    if (!el || !loadingOlderRef.current) return;
    el.scrollTop = el.scrollHeight - prevHeightRef.current;
    loadingOlderRef.current = false;
    // Logged because this is the one effect that can legitimately place the view AWAY from the end, so a
    // report of "opened part-way up" is either this firing when it shouldn't, or something else entirely.
    // `prev` is logged because the placement is this subtraction: without both heights a restore that lands
    // at the top is indistinguishable from one that legitimately restored a short chat.
    noteScroll("restored after paging", { chat: selected, top: Math.round(el.scrollTop), height: el.scrollHeight, prev: prevHeightRef.current });
  }, [messages, selected]);
  // Where a freshly opened chat starts is decided below, not by the follow hook: it scrolls in a passive
  // effect, which runs after the layout effect that places the view, so it used to overwrite the placement —
  // the unread anchor never survived, and the two positions showed one after the other as a jump.
  const [anchoredChat, setAnchoredChat] = useState<string | null>(null);
  const mainFollow = useFollowStream(messagesRef, messages, selected ?? undefined, !!selected && anchoredChat !== selected);
  // The first unread can sit past the messages loaded up front, which is exactly the backlog case this is
  // for. Keep paging back until it appears rather than giving up and landing at the bottom, which would
  // silently drop the user where they'd have been without the feature.
  useEffect(() => {
    if (!selected || !unreadFromId || anchoredChat === selected) return;
    if (messages.some((m) => m.id === unreadFromId)) return;
    if (messages.length < chatLimit) return; // whole history loaded; it isn't there to find
    if (loadingOlderRef.current) return;
    loadingOlderRef.current = true;
    prevHeightRef.current = messagesRef.current?.scrollHeight ?? 0;
    growChatLimit(selected);
  }, [selected, unreadFromId, messages, chatLimit, anchoredChat, growChatLimit]);
  // Place a freshly opened chat exactly once: on its first unread when there is one, otherwise at the end.
  // Both cases are here so there is a single answer to "where does a chat open", and it lands before paint.
  useLayoutEffect(() => {
    const el = messagesRef.current;
    if (!el || !selected || anchoredChat === selected) return;
    if (!chatReady) return; // nothing rendered yet; placing an empty list would just pin it at 0
    if (unreadFromId) {
      const node = el.querySelector<HTMLElement>("[data-unread-anchor]");
      if (!node) return; // not loaded yet — a later snapshot or a page-back will bring it in
      scrollNodeToTop(el, node);
      // Placed away from the end deliberately, so this reader is NOT following: the flag still reads true
      // from the per-chat reset, and anything that re-pins on it (a late image, an arriving message) would
      // drag them straight down off the divider they were just put on.
      mainFollow.nearBottom.current = false;
      setAnchoredChat(selected);
      noteScroll("placed on divider", { chat: selected, top: Math.round(el.scrollTop), height: el.scrollHeight });
      return;
    }
    setAnchoredChat(selected);
    noteScroll("placing at end", { chat: selected, height: el.scrollHeight, msgs: messages.length, limit: chatLimit });
    const release = holdAtBottom(el);
    return () => {
      release();
      noteScroll("left chat", { chat: selected, fromBottom: Math.round(el.scrollHeight - el.clientHeight - el.scrollTop) });
    };
  }, [selected, unreadFromId, messages, chatReady, anchoredChat, mainFollow.nearBottom, chatLimit]);
  const ideaFollow = useFollowStream(ideaScrollRef, ideaMessages, openIdea ? `${selected}/${openIdea}` : undefined);
  // A group thread opens the way a 1:1 does — on its first unread when there is one, otherwise at the end —
  // so the rule it draws is somewhere you actually arrive rather than something to scroll up and find.
  const [anchoredGroup, setAnchoredGroup] = useState<string | null>(null);
  const groupFollow = useFollowStream(
    groupScrollRef,
    groupMessages,
    selectedGroup ?? undefined,
    !!selectedGroup && anchoredGroup !== selectedGroup,
  );
  useLayoutEffect(() => {
    const el = groupScrollRef.current;
    if (!el || !selectedGroup || anchoredGroup === selectedGroup) return;
    if (groupMessages.length === 0) return; // nothing rendered yet
    if (groupUnreadFromId) {
      const node = el.querySelector<HTMLElement>("[data-unread-anchor]");
      if (!node) return;
      scrollNodeToTop(el, node);
      groupFollow.nearBottom.current = false; // same as the main chat: anchored means not following
      setAnchoredGroup(selectedGroup);
      return;
    }
    setAnchoredGroup(selectedGroup);
    return holdAtBottom(el);
  }, [selectedGroup, groupUnreadFromId, groupMessages, anchoredGroup, groupFollow.nearBottom]);

  function onMessagesScroll() {
    const el = messagesRef.current;
    if (!el || !selected) return;
    mainFollow.onScroll();
    // Only page once the chat has settled. A scroll fired while it is still loading is the browser clamping
    // scrollTop against a list that is still changing size, not the reader asking for history.
    if (!chatReady || anchoredChat !== selected) return;
    if (el.scrollTop < 60 && !loadingOlderRef.current && messages.length >= chatLimit) {
      loadingOlderRef.current = true;
      prevHeightRef.current = el.scrollHeight;
      growChatLimit(selected);
    }
  }

  // Silently (re)register the web push token when the tab already has notification permission — never prompt
  // on load. No-ops until VAPID_KEY is configured. The pair panel has an explicit opt-in button.
  useEffect(() => {
    if (pairings.length) void enableWebPush(pairings, false);
  }, [pairings]);

  async function sendIdea() {
    if (!selected || !openIdea || !draft.trim()) return;
    const [pid, sid] = selected.split("/");
    // Clear the input OPTIMISTICALLY (before the Firestore round-trip) so it empties instantly, not after 1-2s.
    const text = draft;
    setDraft("");
    await clients.current.get(pid)?.sendChannelMessage(sid, openIdea, text);
  }

  const fileRef = useRef<HTMLInputElement>(null);
  const ideaFileRef = useRef<HTMLInputElement>(null);
  const selectedClient = selected ? (clients.current.get(selected.split("/")[0]) ?? null) : null;
  const selectedSession = sessions.find((s) => `${s.pairingId}/${s.sessionId}` === selected);
  // The sessions on the same machine, as an @ names them: the ones this agent can reach.
  const sessionNames = useMemo<Mention[]>(
    () =>
      selectedSession
        ? sessions
            .filter((s) => s.pairingId === selectedSession.pairingId)
            .map((s) => ({
              name: mentionName(s.label, s.parentSessionId ? sessions.find((p) => p.pairingId === s.pairingId && p.sessionId === s.parentSessionId)?.label : undefined),
              session: s.sessionId,
            }))
        : [],
    [sessions, selectedSession],
  );
  const mentionables = useMemo(
    () => sessionNames.filter((m) => m.session !== selectedSession?.sessionId),
    [sessionNames, selectedSession],
  );
  // Session and group selection are mutually exclusive views — picking one clears the other.
  const pickSession = (key: string | null) => {
    setSelectedGroup(null);
    setSelected(key);
  };
  const pickGroup = (key: string) => {
    setSelected(null);
    setSelectedGroup(key);
  };
  const selectedGroupEntry = groups.find((g) => `${g.pairingId}/${g.group.id}` === selectedGroup) ?? null;
  // Resolve a participant sessionId to a display name: prefer the LIVE session label, fall back to the roster
  // label sealed into the group at creation (for sessions not currently mirrored here).
  const sessionLabels = new Map(sessions.map((s) => [s.sessionId, s.label]));
  const resolveSender = (id: string): string =>
    sessionLabels.get(id) ?? selectedGroupEntry?.group.participants.find((p) => p.id === id)?.label ?? "";
  // The groups the OPEN session participates in — a quick-nav strip inside its chat.
  const sessionGroups = selectedSession
    ? groups.filter(
        (g) => g.pairingId === selectedSession.pairingId && g.group.participants.some((p) => p.id === selectedSession.sessionId),
      )
    : [];
  // Order by LAST MESSAGE time (from the previews) — stable, unlike the 30s heartbeat. Sessions with no
  // messages fall back to their start time (also stable), never lastSeen — mirrors the phone.
  const orderedSessions = [...sessions].sort((a, b) => {
    const ka = `${a.pairingId}/${a.sessionId}`;
    const kb = `${b.pairingId}/${b.sessionId}`;
    return (previews[kb]?.ts ?? b.startedAt) - (previews[ka]?.ts ?? a.startedAt);
  });

  // Answered vs merely dismissed — a card that was cleared without a decision must not read as decided.
  const chatCardStates = cardStates(messages);
  const ideaCardStates = cardStates(ideaMessages);

  // Writes the same marker the phone writes, so clearing a card on either client collapses it on both.
  async function onCardDismiss(cardId: string, channelId?: string) {
    if (!selected) return;
    const [pid, sid] = selected.split("/");
    await clients.current.get(pid)?.dismissCard(sid, cardId, channelId);
  }

  // ⌘K palette: jump to any session, or an idea of the current session.
  const paletteItems: Array<{ type: "session" | "idea"; key: string; label: string }> = paletteOpen
    ? [
        ...orderedSessions.map((s) => ({
          type: "session" as const,
          key: `${s.pairingId}/${s.sessionId}`,
          label: s.label,
        })),
        ...channels.map((c) => ({ type: "idea" as const, key: c.id, label: c.title || "(untitled)" })),
      ].filter((it) => {
        const q = paletteQuery.trim().toLowerCase();
        return !q || it.label.toLowerCase().includes(q);
      })
    : [];
  function activatePalette(it: { type: "session" | "idea"; key: string }) {
    if (it.type === "session") {
      setSelected(it.key);
      setView("chat");
    } else {
      setView("ideas");
      setOpenIdea(it.key);
    }
    setPaletteOpen(false);
  }
  const loadImage = (att: string, mime: string) => selectedClient?.loadImage(att, mime) ?? Promise.resolve(null);

  function addPair() {
    const p = decodePairingPayload(pairInput.trim());
    if (!p) {
      setErr("Couldn't read that pairing key.");
      return;
    }
    addPairing(p);
    setPairings(loadPairings());
    setPairInput("");
    setShowPair(false);
    setErr(null);
  }

  function removePair(pairingId: string) {
    setPairings(removePairing(pairingId));
    forgetPairingReads(pairingId);
    clients.current.delete(pairingId);
    if (selected?.startsWith(`${pairingId}/`)) setSelected(null);
    setErr(null);
  }

  function onMirrored(ps: PairingPayload[]) {
    let list = loadPairings();
    for (const p of ps) list = addPairing(p);
    setPairings(list);
    setShowMirror(false);
    setShowPair(false);
    setErr(ps.length ? null : "The phone had no pairings to mirror.");
  }

  async function send(mentions?: Mention[]) {
    if (!selected || !draft.trim()) return;
    const [pid, sid] = selected.split("/");
    // Clear the input OPTIMISTICALLY (before the Firestore round-trip) so it empties instantly, not after 1-2s.
    const text = draft;
    setDraft("");
    await clients.current.get(pid)?.sendMessage(sid, text, mentions ? { mentions } : undefined);
  }

  // Tapping a quick-reply chip or a decision-card button sends the label back (cards also carry the
  // card/option correlation). Routes to the open idea channel when in the ideas view.
  async function onOption(text: string, extra?: { cardId?: string; optionId?: string }) {
    if (!selected) return;
    const [pid, sid] = selected.split("/");
    const client = clients.current.get(pid);
    if (view === "ideas" && openIdea) await client?.sendChannelMessage(sid, openIdea, text, extra);
    else await client?.sendMessage(sid, text, extra);
  }

  // Open the attached-document reader: show it immediately (loading), then load + decrypt the referenced
  // document from the channel's documents collection.
  function openDocReader(title: string, channelId: string, docId: string) {
    setDocReader({ title, content: null, loading: true, channelId, docId, revisions: [], comments: [] });
    if (!selected) return;
    const [pid, sid] = selected.split("/");
    const client = clients.current.get(pid);
    void client
      ?.loadDocument(sid, channelId, docId)
      .then((content) => setDocReader((prev) => (prev && prev.docId === docId ? { ...prev, content, loading: false } : prev)));
    // The history and the comments load alongside the text rather than gating it: neither is useful before
    // there is something to read, and a slow list must not hold up the document the user actually asked for.
    void client?.loadDocumentRevisions(sid, channelId, docId).then((revisions) => {
      setDocReader((prev) => (prev && prev.channelId === channelId ? { ...prev, revisions } : prev));
      const thread = revisions[0]?.id;
      if (thread) void refreshComments(sid, channelId, thread, pid);
    });
  }

  /** (Re)load the comments on a document thread — after opening one, and after submitting a round. */
  function refreshComments(sid: string, channelId: string, thread: string, pid: string) {
    return clients.current
      .get(pid)
      ?.loadComments(sid, channelId, thread)
      .then((comments) => setDocReader((prev) => (prev && prev.channelId === channelId ? { ...prev, comments } : prev)));
  }

  /** Submit a review round: every pending comment plus one chat message announcing it. */
  async function submitDocReview(comments: Array<{ quote: string; text: string; parent?: string }>) {
    if (!selected || !docReader) return;
    const [pid, sid] = selected.split("/");
    const { channelId, docId, revisions, title } = docReader;
    const thread = revisions[0]?.id ?? docId;
    const current = revisions.find((r) => r.id === docId);
    // The document's own name, not the title of the message it arrived on: the summary is what the agent
    // reads to know which document was reviewed, and the message title can be anything.
    const name = current?.name?.trim() || title;
    await clients.current.get(pid)?.submitReview(sid, channelId, { id: docId, thread, rev: current?.rev ?? 1, name }, comments);
    await refreshComments(sid, channelId, thread, pid);
  }

  /** Another revision's text, for comparing it with the one in the reader. */
  function loadDocRevision(docId: string): Promise<string | null> {
    if (!selected || !docReader) return Promise.resolve(null);
    const [pid, sid] = selected.split("/");
    return clients.current.get(pid)?.loadDocument(sid, docReader.channelId, docId) ?? Promise.resolve(null);
  }

  /** Switch the open reader to another revision of the same document. */
  function pickDocRevision(docId: string) {
    if (!selected || !docReader) return;
    const [pid, sid] = selected.split("/");
    const { channelId } = docReader;
    setDocReader((prev) => (prev ? { ...prev, docId, content: null, loading: true } : prev));
    void clients.current
      .get(pid)
      ?.loadDocument(sid, channelId, docId)
      .then((content) => setDocReader((prev) => (prev && prev.docId === docId ? { ...prev, content, loading: false } : prev)));
  }

  // Image send is two-step (like the phone): preview + optional caption, then upload on Send. Supports a
  // gallery of several images in one message.
  function queueImages(files: File[]) {
    const imgs = files.filter((f) => f.type.startsWith("image/"));
    if (!imgs.length) return;
    setImageCaption("");
    setPendingImage({ files: imgs, urls: imgs.map((f) => URL.createObjectURL(f)) });
  }
  /**
   * What the attach button hands over: pictures wait for a caption, anything else goes straight out.
   *
   * A picture has something to look at before it is sent, so it gets the preview sheet. A file has only its
   * name, and a sheet showing a name is a prompt to confirm a filename.
   */
  function attachFiles(files: File[]) {
    queueImages(files);
    for (const file of files.filter((f) => !f.type.startsWith("image/"))) void sendOneFile(file);
  }
  async function sendOneFile(file: File) {
    if (!selected) return;
    // Said out loud rather than dropped: the bytes are held as one base64 string on the way up, so a large
    // file is a tab that stops responding, and a file that silently never sends is worse than a refusal.
    if (file.size > MAX_ATTACHMENT_BYTES) {
      window.alert(`${file.name} is too big to send (max ${humanSize(MAX_ATTACHMENT_BYTES)}).`);
      return;
    }
    const [pid, sid] = selected.split("/");
    const channelId = view === "ideas" && openIdea ? openIdea : undefined;
    await clients.current.get(pid)?.sendFile(sid, file, "", channelId);
  }
  function cancelPendingImage() {
    pendingImage?.urls.forEach((u) => URL.revokeObjectURL(u));
    setPendingImage(null);
    setImageCaption("");
  }
  async function sendPendingImage() {
    if (!pendingImage || !selected) return;
    const [pid, sid] = selected.split("/");
    const { files, urls } = pendingImage;
    const caption = imageCaption.trim();
    setPendingImage(null);
    setImageCaption("");
    urls.forEach((u) => URL.revokeObjectURL(u));
    const channelId = view === "ideas" && openIdea ? openIdea : undefined;
    await clients.current.get(pid)?.sendImages(sid, files, caption, channelId);
  }

  return (
    <div className="app">
      <header className="topbar">
        <h1 className="home" onClick={() => setSelected(null)} title="Back to dashboard">
          Failover
        </h1>
        {pairings.length > 0 && (
          <button className="pair-toggle" onClick={() => setShowPair((s) => !s)} title="Manage pairings">
            {showPair ? "×" : "＋"}
          </button>
        )}
      </header>
      {showPair && pairings.length > 0 && (
        <PairPanel
          pairInput={pairInput}
          setPairInput={setPairInput}
          onAddPair={addPair}
          pairings={pairings}
          onRemovePair={removePair}
          onMirror={() => setShowMirror(true)}
          onEnableNotifications={async () => {
            const s = await enableWebPush(pairings, true);
            setErr(
              s === "registered"
                ? null
                : s === "unconfigured"
                  ? "Web push isn't set up yet (needs a VAPID key)."
                  : s === "denied"
                    ? "Notifications are blocked for this site."
                    : "Web push isn't supported in this browser.",
            );
          }}
        />
      )}
      {showMirror && <MirrorModal onClose={() => setShowMirror(false)} onMirrored={onMirrored} />}
      {pendingImage && (
        <ImagePreviewModal
          pendingImage={pendingImage}
          caption={imageCaption}
          setCaption={setImageCaption}
          onSend={sendPendingImage}
          onCancel={cancelPendingImage}
        />
      )}
      {paletteOpen && (
        <CommandPalette
          query={paletteQuery}
          setQuery={setPaletteQuery}
          items={paletteItems}
          onActivate={activatePalette}
          onClose={() => setPaletteOpen(false)}
        />
      )}
      {lightbox && <Lightbox src={lightbox} onClose={() => setLightbox(null)} />}
      {docReader && (
        <DocReader
          doc={docReader}
          onClose={() => setDocReader(null)}
          onPickRevision={pickDocRevision}
          onSubmitReview={submitDocReview}
          loadRevision={loadDocRevision}
        />
      )}
      {showNewGroup && (
        <NewGroupModal
          sessions={sessions}
          onClose={() => setShowNewGroup(false)}
          onCreate={async (pairingId, title, agentSessionIds) => {
            const roster = new Map(sessions.map((s) => [s.sessionId, { label: s.label }]));
            const gid = await clients.current.get(pairingId)?.createGroup(title, agentSessionIds, roster);
            setShowNewGroup(false);
            if (gid) pickGroup(`${pairingId}/${gid}`);
          }}
        />
      )}
      {showNewAgent && selected && (
        <NewAgentModal
          sessionLabel={selectedSession?.label ?? "This session"}
          onClose={() => setShowNewAgent(false)}
          onStart={async (agent) => {
            const [pid, sid] = selected.split("/");
            await clients.current.get(pid)?.requestAgent(sid, agent);
            setShowNewAgent(false);
          }}
        />
      )}
      {err && <p className="error">{err}</p>}
      {!ready ? (
        <main className="content">
          <p className="muted">Connecting…</p>
        </main>
      ) : pairings.length === 0 ? (
        <main className="content pair-empty">
          <p className="muted">
            No pairings yet. Paste a pairing key (the <code>cc1:…</code> from <code>npm run pair</code>, or a{" "}
            <code>…/pair#cc1:…</code> link), or open a pairing link.
          </p>
          <div className="composer">
            <input
              value={pairInput}
              onChange={(e) => setPairInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && addPair()}
              placeholder="cc1:…"
            />
            <button onClick={addPair}>Pair</button>
          </div>
          <p className="muted" style={{ marginTop: 16 }}>
            Or mirror everything from your phone:{" "}
            <button className="linkish" onClick={() => setShowMirror(true)}>
              Mirror from phone
            </button>
          </p>
        </main>
      ) : (
        <div className={`split${view === "ideas" && openIdea ? " ideas-detail" : ""}`}>
          <Sidebar
            sessionsReady={sessionsReady}
            sessions={sessions}
            orderedSessions={orderedSessions}
            sessionQuery={sessionQuery}
            setSessionQuery={setSessionQuery}
            previews={previews}
            unreads={unreads}
            selected={selected}
            setSelected={pickSession}
            groups={groups}
            groupUnreads={groupUnreads}
            selectedGroup={selectedGroup}
            onSelectGroup={pickGroup}
            onNewGroup={() => setShowNewGroup(true)}
          />
          <section className="chat">
            {selectedGroupEntry ? (
              <GroupThread
                group={selectedGroupEntry.group}
                messages={groupMessages}
                unreadFromId={groupUnreadFromId}
                resolveSender={resolveSender}
                endRef={endRef}
                scrollRef={groupScrollRef}
                onScroll={groupFollow.onScroll}
                load={loadImage}
                onOpen={setLightbox}
                onSend={(text) => {
                  const [pid, gid] = selectedGroup!.split("/");
                  void clients.current.get(pid)?.sendGroupMessage(gid, text);
                }}
                onBack={() => setSelectedGroup(null)}
                onClose={() => {
                  const [pid, gid] = selectedGroup!.split("/");
                  void clients.current.get(pid)?.deleteGroup(gid);
                  setSelectedGroup(null);
                }}
              />
            ) : !selected ? (
              <Dashboard
                orderedSessions={orderedSessions}
                sessionTodos={sessionTodos}
                previews={previews}
                unreads={unreads}
                setSelected={pickSession}
              />
            ) : (
              <>
                <ChatHeader
                  session={selectedSession}
                  messages={messages}
                  onNewAgent={() => setShowNewAgent(true)}
                  onCancelScheduled={(id) => {
                    if (!selected) return;
                    const [pid, sid] = selected.split("/");
                    void clients.current.get(pid)?.cancelScheduled(sid, id);
                  }}
                  onArchive={async () => {
                    if (!selected) return;
                    const [pid, sid] = selected.split("/");
                    await clients.current.get(pid)?.archiveSession(sid);
                    setSelected(null);
                  }}
                />
                <SessionGroupsBar groups={sessionGroups} onOpenGroup={pickGroup} />
                <ChatTabs
                  view={view}
                  setView={setView}
                  setOpenIdea={setOpenIdea}
                  ideaCount={channels.length}
                  activeTodos={todos.filter((t) => t.status !== "completed").length}
                  ideas={!selectedSession?.managed}
                />
                {view === "todos" ? (
                  <TodosRail
                    todos={todos}
                    inProgressIdea={channels.find((c) => c.status === "in_progress")}
                    agents={agents}
                    inline
                  />
                ) : view === "chat" || selectedSession?.managed ? (
                  <>
                    <NewMessagesHint show={mainFollow.firstNewId !== null} onClick={mainFollow.jumpToFirstNew} />
                    <ChatMessages
                      unreadFromId={unreadFromId}
                      live={selectedSession?.online && !selectedSession.idle ? selectedSession.activityUpdate : undefined}
                      messages={messages}
                      chatReady={chatReady}
                      chatLimit={chatLimit}
                      messagesRef={messagesRef}
                      onScroll={onMessagesScroll}
                      endRef={endRef}
                      load={loadImage}
                      onOpen={setLightbox}
                      onOpenDoc={(title, docId) => openDocReader(title, "main", docId)}
                      onOption={onOption}
                      cardStates={chatCardStates}
                      onCardDismiss={(cardId) => void onCardDismiss(cardId)}
                      onOpenSession={(session) => selectedSession && pickSession(`${selectedSession.pairingId}/${session}`)}
                      sessionNames={sessionNames}
                    />
                    <Composer
                      fileRef={fileRef}
                      draft={draft}
                      setDraft={setDraft}
                      onSend={(mentions) => void send(mentions)}
                      onFiles={(files) => {
                        if (files.length && selected) attachFiles(files);
                      }}
                      mentionables={mentionables}
                    />
                  </>
                ) : openIdea ? (
                  <>
                    <IdeaThreadHeader
                      title={channels.find((c) => c.id === openIdea)?.title || "(untitled)"}
                      status={channels.find((c) => c.id === openIdea)?.status ?? "draft"}
                      onBack={() => setOpenIdea(null)}
                      onStatusChange={async (status) => {
                        if (!selected || !openIdea) return;
                        const [pid, sid] = selected.split("/");
                        await clients.current.get(pid)?.setIdeaStatus(sid, openIdea, status);
                      }}
                    />
                    <NewMessagesHint show={ideaFollow.firstNewId !== null} onClick={ideaFollow.jumpToFirstNew} />
                    <IdeaMessages
                      messages={ideaMessages}
                      endRef={endRef}
                      scrollRef={ideaScrollRef}
                      onScroll={ideaFollow.onScroll}
                      load={loadImage}
                      onOpen={setLightbox}
                      onOpenDoc={(title, docId) => openIdea && openDocReader(title, openIdea, docId)}
                      onOption={onOption}
                      cardStates={ideaCardStates}
                      onCardDismiss={(cardId) => openIdea && void onCardDismiss(cardId, openIdea)}
                    />
                    <Composer
                      fileRef={ideaFileRef}
                      draft={draft}
                      setDraft={setDraft}
                      onSend={sendIdea}
                      onFiles={(files) => {
                        if (files.length && selected) attachFiles(files);
                      }}
                      placeholder="Reply"
                    />
                  </>
                ) : (
                  <IdeaBoard
                    channels={channels}
                    ideaQuery={ideaQuery}
                    setIdeaQuery={setIdeaQuery}
                    dragCol={dragCol}
                    setDragCol={setDragCol}
                    onOpenIdea={setOpenIdea}
                    onDropCard={async (id, col) => {
                      if (!selected) return;
                      const [pid, sid] = selected.split("/");
                      await clients.current.get(pid)?.setIdeaStatus(sid, id, col);
                    }}
                  />
                )}
              </>
            )}
          </section>
          {selected && view === "chat" && (
            <TodosRail todos={todos} inProgressIdea={channels.find((c) => c.status === "in_progress")} agents={agents} />
          )}
          {selected && view === "ideas" && openIdea && (
            <SpecRail doc={channels.find((c) => c.id === openIdea)?.doc ?? ""} />
          )}
        </div>
      )}
    </div>
  );
}
